import { useEffect, useState } from "preact/hooks";
import { articleHistoryDB, type ArticleHistoryItem } from "../utils/history.ts";

export default function ArticleHistory() {
  const [articles, setArticles] = useState<ArticleHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const history = await articleHistoryDB.getArticles(20);
      setArticles(history);
    } catch (error) {
      console.error("Failed to load article history:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    if (confirm("Are you sure you want to clear all article history?")) {
      try {
        await articleHistoryDB.clearHistory();
        setArticles([]);
      } catch (error) {
        console.error("Failed to clear history:", error);
      }
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div class="rounded-lg shadow p-6 border bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700 transition-colors duration-200">
        <h2 class="text-xl font-semibold mb-4 text-slate-900 dark:text-white">
          Recent Articles
        </h2>
        <div class="text-center text-gray-500 dark:text-gray-400">
          Loading history...
        </div>
      </div>
    );
  }

  return (
    <div class="rounded-lg shadow p-6 border bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700 transition-colors duration-200">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold text-slate-900 dark:text-white">
          Recent Articles
        </h2>
        {articles.length > 0 && (
          <button
            onClick={clearHistory}
            class="text-sm text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300 underline"
          >
            Clear History
          </button>
        )}
      </div>

      {articles.length === 0 ? (
        <div class="text-center text-gray-500 dark:text-gray-400 py-8">
          <p>No articles viewed yet.</p>
          <p class="text-sm mt-2">
            Articles you view will appear here for quick access.
          </p>
        </div>
      ) : (
        <div class="space-y-4">
          {articles.map((article) => (
            <div
              key={article.id}
              class="border rounded-lg p-4 transition-colors bg-white border-gray-200 hover:bg-gray-50 dark:bg-transparent dark:border-gray-600 dark:hover:bg-gray-700"
            >
              <div class="flex justify-between items-start mb-2">
                <h3 class="font-medium text-slate-900 dark:text-white line-clamp-2 flex-1 mr-4">
                  <a
                    href={`/article?url=${encodeURIComponent(article.url)}&userAgent=${encodeURIComponent(article.userAgent)}${article.bypassPaywall ? "&bypassPaywall=true" : ""}`}
                    class="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  >
                    {article.title}
                  </a>
                </h3>
                <div class="flex items-center gap-2 shrink-0">
                  {article.bypassPaywall && (
                    <span class="inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                      Medium
                    </span>
                  )}
                  <span class="text-xs text-gray-500 whitespace-nowrap">
                    {formatDate(article.date)}
                  </span>
                </div>
              </div>

              <p class="text-sm text-gray-700 dark:text-gray-300 line-clamp-3 mb-3">
                {article.content}
              </p>

              <div class="flex gap-2 text-xs">
                <a
                  href={`/article?url=${encodeURIComponent(article.url)}&userAgent=${encodeURIComponent(article.userAgent)}${article.bypassPaywall ? "&bypassPaywall=true" : ""}`}
                  class="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 underline"
                >
                  View HTML
                </a>
                <span class="text-gray-500">•</span>
                <a
                  href={`/markdown?url=${encodeURIComponent(article.url)}&userAgent=${encodeURIComponent(article.userAgent)}${article.bypassPaywall ? "&bypassPaywall=true" : ""}`}
                  class="text-green-600 hover:text-green-500 dark:text-green-400 dark:hover:text-green-300 underline"
                >
                  View Markdown
                </a>
                <span class="text-gray-500">•</span>
                <a
                  href={`/api/html?url=${encodeURIComponent(article.url)}&userAgent=${encodeURIComponent(article.userAgent)}${article.bypassPaywall ? "&bypassPaywall=true" : ""}`}
                  target="_blank"
                  class="text-purple-600 hover:text-purple-500 dark:text-purple-400 dark:hover:text-purple-300 underline"
                >
                  Plain HTML
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
